// src/components/GoToTop.js
//
// Reusable dashboard-wide "Go to Top" control. Mounted once at the app shell
// (App.js) so it covers every current and future route without per-page code.
// It appears only after the page has been scrolled a meaningful distance and
// hides again near the top, so short pages never show it. Fixed to the lower
// right, raised above the lower-right Support control so it never covers it.
//
// Smooth scrolling respects prefers-reduced-motion. Keyboard accessible with an
// accessible name. No browser storage, no external packages, no optional
// chaining or nullish coalescing (matching portal conventions).

import React, { useState, useEffect } from 'react';

// Pixels scrolled before the control becomes useful / visible.
const SHOW_AFTER = 400;

export default function GoToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setVisible(y > SHOW_AFTER);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const handleClick = () => {
    const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    window.scrollTo({ top: 0, left: 0, behavior: reduce ? 'auto' : 'smooth' });
  };

  if (!visible) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Go to top of page"
      title="Go to top"
      style={btn}
      onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      <span aria-hidden="true" style={arrow}>{'\u2191'}</span>
    </button>
  );
}

const btn = {
  position: 'fixed',
  right: 24,
  bottom: 96,
  width: 46,
  height: 46,
  borderRadius: 23,
  border: '1px solid rgba(255,255,255,0.22)',
  background: 'linear-gradient(160deg, rgba(124,58,237,0.96) 0%, rgba(14,165,233,0.96) 100%)',
  color: '#ffffff',
  cursor: 'pointer',
  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  zIndex: 250,
  transition: 'transform 0.18s ease, filter 0.18s ease',
};

const arrow = {
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1,
  transform: 'translateY(-1px)',
};
